import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  Grid, 
  Paper, 
  IconButton, 
  Card, 
  CardMedia, 
  CardContent, 
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  DialogContentText,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAppContext } from '../context/AppContext';
import { MetadataAttribute, UniqueNFT } from '../types/index';

const UniqueNFTUpload: React.FC = () => {
  const { 
    uniqueNFTs, 
    addUniqueNFT, 
    removeUniqueNFT, 
    updateUniqueNFT,
    config,
    setCurrentStep,
    currentStep
  } = useAppContext();

  const [openDialog, setOpenDialog] = useState(false);
  const [confirmNoAttributesDialog, setConfirmNoAttributesDialog] = useState(false);
  const [editingNFT, setEditingNFT] = useState<UniqueNFT | null>(null);
  const [nftName, setNftName] = useState('');
  const [nftDescription, setNftDescription] = useState('');
  const [customId, setCustomId] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [attributes, setAttributes] = useState<MetadataAttribute[]>([]);
  const [newTraitType, setNewTraitType] = useState('');
  const [newTraitValue, setNewTraitValue] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form when dialog closes
  useEffect(() => {
    if (!openDialog) {
      resetForm();
    }
  }, [openDialog]);

  // Set form values when editing an existing NFT
  useEffect(() => {
    if (editingNFT) {
      setNftName(editingNFT.name);
      setNftDescription(editingNFT.description || '');
      setAttributes(editingNFT.attributes);
      setCustomId(editingNFT.customId?.toString() || '');
      if (editingNFT.imageDataUrl) {
        setImagePreview(editingNFT.imageDataUrl);
      }
    }
  }, [editingNFT]);

  const resetForm = () => {
    setNftName('');
    setNftDescription('');
    setSelectedImage(null);
    setImagePreview(null);
    setAttributes([]);
    setNewTraitType('');
    setNewTraitValue('');
    setCustomId('');
    setEditingNFT(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && event.target.result) {
          setImagePreview(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddAttribute = () => {
    if (newTraitType.trim() && newTraitValue.trim()) {
      setAttributes([
        ...attributes,
        { trait_type: newTraitType.trim(), value: newTraitValue.trim() }
      ]);
      setNewTraitType('');
      setNewTraitValue('');
    }
  };

  const handleRemoveAttribute = (index: number) => {
    setAttributes(attributes.filter((_, i) => i !== index));
  };

  const handleOpenDialog = (nft?: UniqueNFT) => {
    if (nft) {
      setEditingNFT(nft);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSaveNFT = async () => {
    if (!selectedImage && !editingNFT?.image) {
      alert('Please provide an image for the unique NFT.');
      return;
    }

    // Si aucun attribut n'est présent, demander confirmation
    if (attributes.length === 0) {
      setConfirmNoAttributesDialog(true);
      return;
    }

    // Si des attributs sont présents, sauvegarder directement
    await saveNFT();
  };

  const saveNFT = async () => {
    let imageDataUrl = imagePreview || undefined;
    
    // If we have a new image, create a data URL
    if (selectedImage && !imageDataUrl) {
      imageDataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target && event.target.result) {
            resolve(event.target.result as string);
          }
        };
        reader.readAsDataURL(selectedImage);
      });
    }

    // Parse customId if provided
    const parsedCustomId = customId.trim() !== '' ? parseInt(customId, 10) : undefined;
    
    // Use a default name if none is provided, include customId in the name if available
    const finalName = nftName.trim() || 
      (parsedCustomId ? `${config.namePrefix} #${parsedCustomId}` : `${config.namePrefix}`);

    if (editingNFT) {
      // Update existing NFT
      updateUniqueNFT(editingNFT.id, {
        name: finalName,
        description: nftDescription || config.description,
        image: selectedImage || editingNFT.image,
        imageDataUrl,
        attributes,
        customId: parsedCustomId
      });
    } else {
      // Add new NFT
      const newNFT: UniqueNFT = {
        id: Date.now(), // Use timestamp as a simple unique ID
        name: finalName,
        description: nftDescription || config.description,
        image: selectedImage,
        imageDataUrl,
        attributes,
        customId: parsedCustomId
      };
      addUniqueNFT(newNFT);
    }

    handleCloseDialog();
    setConfirmNoAttributesDialog(false);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmNoAttributesDialog(false);
  };

  const handleContinue = () => {
    // Passer à l'étape suivante
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    // Revenir à l'étape précédente
    setCurrentStep(currentStep - 1);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Unique NFTs (1/1) - Optional
      </Typography>
      
      <Typography variant="body1" paragraph>
        Add unique one-of-one NFTs to your collection. These visuals will be integrated into your generated collection. This step is optional.
      </Typography>
      
      <Button 
        variant="contained" 
        startIcon={<AddIcon />} 
        onClick={() => handleOpenDialog()}
        sx={{ mb: 3 }}
      >
        Add Unique NFT
      </Button>
      
      {uniqueNFTs.length > 0 ? (
        <Grid container spacing={3}>
          {uniqueNFTs.map((nft) => (
            <Grid item xs={12} sm={6} md={4} key={nft.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {nft.imageDataUrl && (
                  <CardMedia
                    component="img"
                    height="200"
                    image={nft.imageDataUrl}
                    alt={nft.name}
                    sx={{
                      imageRendering: 'pixelated',
                      objectFit: 'contain'
                    }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h6" component="div">
                    {nft.name}
                  </Typography>
                  {nft.customId !== undefined && (
                    <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                      Custom ID: #{nft.customId}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {nft.description || config.description}
                  </Typography>
                  
                  {nft.attributes.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                        Attributes:
                      </Typography>
                      <List dense>
                        {nft.attributes.map((attr, index) => (
                          <ListItem key={index} disablePadding>
                            <ListItemText 
                              primary={`${attr.trait_type}: ${attr.value}`} 
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(nft)}
                  >
                    Edit
                  </Button>
                  <Button 
                    size="small" 
                    color="error" 
                    startIcon={<DeleteIcon />}
                    onClick={() => removeUniqueNFT(nft.id)}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No unique NFTs have been added. Click "Add Unique NFT" to get started.
          </Typography>
        </Paper>
      )}
      
      {/* Dialog for adding/editing NFTs */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingNFT ? 'Edit Unique NFT' : 'Add Unique NFT'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="NFT Name (optional)"
                fullWidth
                value={nftName}
                onChange={(e) => setNftName(e.target.value)}
                margin="normal"
                helperText={`If empty, the collection name will be used: "${config.namePrefix}"`}
              />
              
              <TextField
                label="Description (optional)"
                fullWidth
                multiline
                rows={4}
                value={nftDescription}
                onChange={(e) => setNftDescription(e.target.value)}
                margin="normal"
                helperText={`If empty, the general collection description will be used: "${config.description}"`}
              />
              
              <TextField
                label="Custom ID (optional)"
                fullWidth
                type="number"
                value={customId}
                onChange={(e) => setCustomId(e.target.value)}
                margin="normal"
                helperText="Specify a custom ID number for this NFT. If left blank, a random ID will be assigned."
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
              
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Image
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleImageChange}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ mb: 2 }}
                >
                  {editingNFT?.image ? 'Change Image' : 'Select Image'}
                </Button>
                
                {imagePreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        objectFit: 'contain',
                        imageRendering: 'pixelated',
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                      }} 
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Attributes
              </Typography>
              
              <Box sx={{ display: 'flex', mb: 2 }}>
                <TextField
                  label="Trait Type"
                  value={newTraitType}
                  onChange={(e) => setNewTraitType(e.target.value)}
                  size="small"
                  sx={{ mr: 1, flexGrow: 1 }}
                />
                <TextField
                  label="Value"
                  value={newTraitValue}
                  onChange={(e) => setNewTraitValue(e.target.value)}
                  size="small"
                  sx={{ mr: 1, flexGrow: 1 }}
                />
                <Button 
                  variant="contained" 
                  onClick={handleAddAttribute}
                  disabled={!newTraitType.trim() || !newTraitValue.trim()}
                >
                  Add
                </Button>
              </Box>
              
              {attributes.length > 0 ? (
                <List>
                  {attributes.map((attr, index) => (
                    <ListItem key={index} divider>
                      <ListItemText 
                        primary={attr.trait_type} 
                        secondary={attr.value} 
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          aria-label="delete" 
                          onClick={() => handleRemoveAttribute(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No attributes added. Attributes define the characteristics of your NFT.
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveNFT} 
            variant="contained"
            disabled={!selectedImage && !editingNFT?.image}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue de confirmation pour NFT sans attributs */}
      <Dialog
        open={confirmNoAttributesDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"No Metadata Attributes"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You haven't added any attributes to this NFT. The metadata will not contain any attributes, which may affect compatibility with some platforms and trait visualization.
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Attributes are important for rarity and filtering on marketplaces like OpenSea.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseConfirmDialog} 
            variant="contained" 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={saveNFT} 
            variant="outlined" 
            color="inherit"
            autoFocus
          >
            Continue without attributes
          </Button>
        </DialogActions>
      </Dialog>
      
      <Box sx={{ mb: 2, mt: 4 }}>
        <Box display="flex" justifyContent="space-between">
          <Button 
            variant="outlined" 
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleContinue}
            endIcon={<ArrowForwardIcon />}
          >
            Next: Generate Collection
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default UniqueNFTUpload; 